import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
  answerBlock
} from 'actions';

import Question from 'components/Questionnaires/Question';
import { QuestionTextField, QuestionDropDownField, QuestionYesNoField } from 'components/Questionnaires/fields';
import { requiredValidator, validateQuestion } from 'components/Questionnaires/validators';

export const QuestionW9TaxClassificationBlock = ({ block }) => {
  const dispatch = useDispatch();

  const [answer, setAnswer] = useState(block.answers ?? { classification: '1' });

  const [classification, setClassification] = useState({
    id: 'classification',
    label: 'questionnaire.W9.taxClassification.label',
    component: QuestionDropDownField,
    componentProps: {
      items: 'questionnaire.W9.taxClassification.items',
    },
    default: '1',
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.W9.taxClassification.errorMessage' }
      ]
    },
    answer: answer?.classification
  });
  const [llcClassification, setLlcClassification] = useState({
    id: 'llcClassification',
    label: 'questionnaire.W9.llcClassification.label',
    component: QuestionDropDownField,
    componentProps: {
      items: 'questionnaire.W9.llcClassification.items'
    },
    visible: blockAnswers => blockAnswers?.classification === '6', // LLC Classification
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.W9.llcClassification.errorMessage' }
      ]
    },
    answer: answer?.llcClassification
  });
  const [otherClassification, setOtherClassification] = useState({
    id: 'otherClassification',
    label: 'questionnaire.W9.otherClassification.label',
    placeholder: 'questionnaire.W9.otherClassification.placeholder',
    component: QuestionTextField,
    visible: blockAnswers => blockAnswers?.classification === '7', // Other Classification
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.W9.otherClassification.errorMessage' }
      ]
    },
    answer: answer?.otherClassification
  });
  const [hasForeignPartners, setHasForeignPartners] = useState({
    id: 'hasForeignPartners',
    label: 'questionnaire.W9.hasForeignPartners.label',
    info: {
      instructions: 'questionnaire.W9.hasForeignPartners.info'
    },
    component: QuestionYesNoField,
    visible: blockAnswers => blockAnswers?.classification === '4' || blockAnswers?.classification === '5' || (blockAnswers?.classification === '6' && blockAnswers?.llcClassification === '3'),
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.W9.hasForeignPartners.errorMessage' }
      ]
    },
    answer: answer?.hasForeignPartners
  });

  const blockIdentifier = useMemo(() => ((block && block.id) ? `${ block.id }${ block.index !== undefined ? (`.${ block.index }`) : '' }` : undefined), [block.index]);

  const isValid = () => {
    switch (classification.answer) {
      case '4':
      case '5':
        return hasForeignPartners.answer === undefined ? undefined : hasForeignPartners.answer !== '';
      case '6':
        if (llcClassification.answer === '3') {
          return hasForeignPartners.answer === undefined ? undefined : hasForeignPartners.answer !== '';
        }
        return llcClassification.answer === undefined ? undefined : llcClassification.answer !== '';
      case '7':
        return otherClassification.answer === undefined ? undefined : otherClassification.answer !== '';
      default:
        return true;
    }
  };

  useEffect(() => {
    dispatch(answerBlock(block.id, answer, isValid()));
  }, [answer]);

  useEffect(() => {
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  const onQuestionChange = (q, value) => {
    switch (q.id) {
      case 'classification': {
        const newClassification = { ...q, answer: value };
        validateQuestion(newClassification);
        setClassification(newClassification);
        const newAnswers = { classification: value };
        if (value !== '6') {
          setLlcClassification(Object.assign(llcClassification, { answer: undefined, isValid: undefined, errorMessages: undefined }));
          newAnswers.llcClassification = undefined;
        }
        if (value !== '7') {
          setOtherClassification(Object.assign(otherClassification, { answer: undefined, isValid: undefined, errorMessages: undefined }));
          newAnswers.otherClassification = undefined;
        }
        if (value !== '4' && value !== '5' && value !== '6') {
          setHasForeignPartners(Object.assign(hasForeignPartners, { answer: undefined, isValid: undefined, errorMessages: undefined }));
          newAnswers.hasForeignPartners = undefined;
        }
        setAnswer({ ...answer, ...newAnswers });
        break;
      }
      case 'llcClassification': {
        const newLlcClassification = Object.assign(q, { answer: value });
        validateQuestion(newLlcClassification);
        setLlcClassification(newLlcClassification);
        setAnswer({ ...answer, llcClassification: value });
        break;
      }
      case 'otherClassification': {
        const newOtherClassification = Object.assign(q, { answer: value });
        validateQuestion(newOtherClassification);
        setOtherClassification(newOtherClassification);
        setAnswer({ ...answer, otherClassification: value });
        break;
      }
      case 'hasForeignPartners': {
        const newHasForeignPartners = Object.assign(q, { answer: value });
        validateQuestion(newHasForeignPartners);
        setHasForeignPartners(newHasForeignPartners);
        setAnswer({ ...answer, hasForeignPartners: value });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (block.isForceEdited === true) {
      if (answer?.classification === undefined || answer.classification === '') {
        onQuestionChange({ ...classification, isForceEdited: true }, '');
      }
      if (answer?.classification === '6' && (answer?.llcClassification === undefined || answer?.llcClassification === '')) {
        onQuestionChange({ ...llcClassification, isForceEdited: true }, '');
      }
      if (answer?.classification === '7' && (answer?.otherClassification === undefined || answer?.otherClassification === '')) {
        onQuestionChange({ ...otherClassification, isForceEdited: true }, '');
      }
      if ((answer?.classification === '4' || answer?.classification === '5' || (answer?.classification === '6' && answer?.llcClassification === '3'))
        && (answer?.hasForeignPartners === undefined || answer?.hasForeignPartners === '')) {
        onQuestionChange({ ...hasForeignPartners, isForceEdited: true }, '');
      }
    }
  }, [block.isForceEdited]);

  return (<React.Fragment>
    <Question key={ classification.id } blockId={ blockIdentifier } question={ classification } { ...classification.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    { llcClassification.visible(block.answers) && <Question key={ llcClassification.id } blockId={ blockIdentifier } question={ llcClassification } { ...llcClassification.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
    { otherClassification.visible(block.answers) && <Question key={ otherClassification.id } blockId={ blockIdentifier } question={ otherClassification } { ...otherClassification.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
    { hasForeignPartners.visible(block.answers) && <Question key={ hasForeignPartners.id } blockId={ blockIdentifier } question={ hasForeignPartners } { ...hasForeignPartners.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
  </React.Fragment>);
};

QuestionW9TaxClassificationBlock.displayName = 'QuestionW9TaxClassificationBlock';

QuestionW9TaxClassificationBlock.propTypes = {
  block: PropTypes.shape().isRequired,
};

export default QuestionW9TaxClassificationBlock;
